import { type DotVotingItem } from '../types';
import { Trophy, Award, RotateCcw, Copy, FileText, FileJson, FileCode } from 'lucide-react';

interface ResultsViewProps {
  items: DotVotingItem[];
  dotsPerParticipant: number;
  totalDotsUsed: number;
  onReset: () => void;
}

export function ResultsView({ items, totalDotsUsed, onReset }: ResultsViewProps) {
  // Sort items by vote count (descending)
  const rankedItems = [...items].sort((a, b) => b.votes - a.votes);

  const handleExport = async (format: 'markdown' | 'json' | 'plaintext' | 'clipboard') => {
    let content = '';
    let filename = '';
    let mimeType = '';

    switch (format) {
      case 'markdown':
        content = exportToMarkdown(rankedItems, totalDotsUsed);
        filename = `dot-voting-${new Date().toISOString().split('T')[0]}.md`;
        mimeType = 'text/markdown';
        break;
      case 'json':
        content = exportToJSON(rankedItems, totalDotsUsed);
        filename = `dot-voting-${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
        break;
      case 'plaintext':
        content = exportToPlaintext(rankedItems);
        filename = `dot-voting-${new Date().toISOString().split('T')[0]}.txt`;
        mimeType = 'text/plain';
        break;
      case 'clipboard':
        content = exportToPlaintext(rankedItems);
        await copyToClipboard(content);
        alert('Copied to clipboard!');
        return;
    }

    if (format !== 'clipboard') {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="h-6 w-6 text-[var(--mustard)]" />;
    if (rank === 2) return <Award className="h-6 w-6 text-[var(--text-ter)]" />;
    if (rank === 3) return <Award className="h-6 w-6 text-[var(--mustard)]" />;
    return null;
  };

  // Detect ties
  const ties = new Map<number, DotVotingItem[]>();
  rankedItems.forEach((item) => {
    if (!ties.has(item.votes)) {
      ties.set(item.votes, []);
    }
    ties.get(item.votes)!.push(item);
  });

  const hasTies = Array.from(ties.values()).some((group) => group.length > 1);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2">
          Dot Voting Results
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          Items ranked by number of votes. {totalDotsUsed} total dot{totalDotsUsed !== 1 ? 's' : ''} allocated.
        </p>

        {/* Ties Warning */}
        {hasTies && (
          <div className="mb-6 p-4 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
            <h3 className="font-semibold text-[var(--mustard)] mb-2">Ties Detected</h3>
            <p className="text-sm text-[var(--mustard)]">
              Some items have the same number of votes. In dot voting, ties are acceptable and indicate equal priority.
            </p>
            <div className="mt-3 space-y-2">
              {Array.from(ties.entries())
                .filter(([_, group]) => group.length > 1)
                .map(([votes, group]) => (
                  <div key={votes} className="text-sm">
                    <strong>{votes} votes:</strong> {group.map((item) => item.name).join(', ')}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Ranked List */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Ranked List</h3>
          <div className="space-y-3">
            {rankedItems.map((item, index) => {
              const rank = index + 1;
              const isTied = ties.get(item.votes)?.length > 1;

              return (
                <div
                  key={item.id}
                  className="flex items-center gap-4 p-4 bg-[var(--surface)] border border-[var(--rule)] hover:border-[var(--slate)] transition-colors"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] font-bold text-lg text-[var(--text-sec)]">
                    {getRankIcon(rank) || rank}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[var(--text-pri)] text-lg">
                      {item.name}
                      {isTied && <span className="text-sm text-[var(--text-ter)] ml-2">(tied)</span>}
                    </h3>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-sm text-[var(--text-sec)]">
                        <strong className="text-[var(--text-pri)]">{item.votes}</strong> vote{item.votes !== 1 ? 's' : ''}
                      </span>
                      {totalDotsUsed > 0 && (
                        <span className="text-sm text-[var(--text-ter)]">
                          {((item.votes / totalDotsUsed) * 100).toFixed(1)}% of total votes
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Vote Distribution */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Vote Distribution</h3>
          <div className="bg-[var(--surface)] p-4">
            <div className="space-y-2">
              {rankedItems.map((item) => (
                <div key={item.id} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[var(--text-sec)]">{item.name}</span>
                      <span className="font-semibold text-[var(--text-pri)]">{item.votes}</span>
                    </div>
                    <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
                      <div
                        className="bg-[var(--slate)] h-2 rounded-full transition-all"
                        style={{
                          width: totalDotsUsed > 0 ? `${(item.votes / totalDotsUsed) * 100}%` : '0%',
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Export Options */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Export</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <button
              onClick={() => handleExport('markdown')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileText className="h-5 w-5" />
              <span className="text-xs">Markdown</span>
            </button>
            <button
              onClick={() => handleExport('json')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileJson className="h-5 w-5" />
              <span className="text-xs">JSON</span>
            </button>
            <button
              onClick={() => handleExport('plaintext')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileCode className="h-5 w-5" />
              <span className="text-xs">Plaintext</span>
            </button>
            <button
              onClick={() => handleExport('clipboard')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <Copy className="h-5 w-5" />
              <span className="text-xs">Clipboard</span>
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onReset}
            className="flex-1 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-5 w-5" />
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}

// Export functions
function exportToMarkdown(items: DotVotingItem[], totalDots: number): string {
  let content = `# Dot Voting Results\n\n`;
  content += `**Total Votes:** ${totalDots}\n`;
  content += `**Date:** ${new Date().toLocaleDateString()}\n\n`;
  content += `## Ranked Items\n\n`;

  items.forEach((item, index) => {
    content += `${index + 1}. **${item.name}** - ${item.votes} vote${item.votes !== 1 ? 's' : ''}\n`;
  });

  return content;
}

function exportToJSON(items: DotVotingItem[], totalDots: number): string {
  return JSON.stringify(
    {
      method: 'Dot Voting',
      totalDots,
      date: new Date().toISOString(),
      items: items.map((item, index) => ({
        rank: index + 1,
        name: item.name,
        votes: item.votes,
      })),
    },
    null,
    2
  );
}

function exportToPlaintext(items: DotVotingItem[]): string {
  let content = `Dot Voting Results\n`;
  content += `==================\n\n`;

  items.forEach((item, index) => {
    content += `${index + 1}. ${item.name} (${item.votes} vote${item.votes !== 1 ? 's' : ''})\n`;
  });

  return content;
}

async function copyToClipboard(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}


